// Copyright (c) 2026 Nathan Blackman / Find My Trout. All rights reserved.

import {
  collection,
  doc,
  getDoc,
  setDoc,
  addDoc,
  updateDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  serverTimestamp,
  limit
} from 'firebase/firestore';
import { db } from '../firebase/config';

const CONVERSATIONS = 'conversations';
const MESSAGES = 'messages';

/**
 * Get a deterministic conversation ID for two users (sorted UIDs).
 */
export function getConversationId(uid1, uid2) {
  if (!uid1 || !uid2) return null;
  return [uid1, uid2].sort().join('_');
}

/**
 * Get or create a conversation between two users. Returns conversation id.
 */
export async function getOrCreateConversation(currentUserId, otherUserId) {
  const cid = getConversationId(currentUserId, otherUserId);
  if (!cid) return null;
  const ref = doc(db, CONVERSATIONS, cid);
  const snap = await getDoc(ref);
  if (snap.exists()) return cid;
  await setDoc(ref, {
    participantIds: [currentUserId, otherUserId].sort(),
    lastMessage: null,
    updatedAt: serverTimestamp()
  });
  return cid;
}

/**
 * Subscribe to the list of conversations for the current user (Instagram-style inbox).
 * Returns unsubscribe function.
 */
export function subscribeConversations(currentUserId, onUpdate) {
  if (!currentUserId) return () => {};
  const q = query(
    collection(db, CONVERSATIONS),
    where('participantIds', 'array-contains', currentUserId),
    orderBy('updatedAt', 'desc'),
    limit(100)
  );
  return onSnapshot(q, (snapshot) => {
    const list = snapshot.docs.map((d) => ({
      id: d.id,
      ...d.data(),
      updatedAt: d.data().updatedAt
    }));
    onUpdate(list);
  }, (err) => {
    console.error('subscribeConversations error:', err);
    onUpdate([]);
  });
}

/**
 * Subscribe to messages in a conversation (real-time).
 * Returns unsubscribe function.
 */
export function subscribeMessages(conversationId, onUpdate) {
  if (!conversationId) return () => {};
  const messagesRef = collection(db, CONVERSATIONS, conversationId, MESSAGES);
  const q = query(messagesRef, orderBy('createdAt', 'asc'), limit(500));
  return onSnapshot(q, (snapshot) => {
    const list = snapshot.docs.map((d) => ({
      id: d.id,
      ...d.data(),
      createdAt: d.data().createdAt
    }));
    onUpdate(list);
  }, (err) => {
    console.error('subscribeMessages error:', err);
    onUpdate([]);
  });
}

/**
 * Send a message in a conversation. Creates conversation if needed.
 */
export async function sendMessage(conversationId, { senderId, senderName, text }) {
  if (!conversationId || !senderId || !text?.trim()) return null;
  const messagesRef = collection(db, CONVERSATIONS, conversationId, MESSAGES);
  const convRef = doc(db, CONVERSATIONS, conversationId);
  const payload = {
    senderId,
    senderName: senderName || 'Anonymous',
    text: text.trim(),
    createdAt: serverTimestamp()
  };
  const docRef = await addDoc(messagesRef, payload);
  await updateDoc(convRef, {
    lastMessage: {
      text: payload.text,
      senderId: payload.senderId,
      createdAt: payload.createdAt
    },
    updatedAt: serverTimestamp()
  });
  return docRef.id;
}

/**
 * Get a single conversation doc (e.g. to resolve other participant).
 */
export async function getConversation(conversationId) {
  if (!conversationId) return null;
  const snap = await getDoc(doc(db, CONVERSATIONS, conversationId));
  if (!snap.exists()) return null;
  return { id: snap.id, ...snap.data() };
}

/**
 * Get the other participant's uid from a conversation.
 */
export function getOtherParticipantId(conversation, currentUserId) {
  if (!conversation?.participantIds || !currentUserId) return null;
  return conversation.participantIds.find((id) => id !== currentUserId) || null;
}

/**
 * Get the other participant's uid from a conversation id (e.g. "uidA_uidB").
 */
export function getOtherParticipantIdFromConversationId(conversationId, currentUserId) {
  if (!conversationId || !currentUserId) return null;
  const parts = conversationId.split('_');
  return parts.find((id) => id !== currentUserId) || null;
}

/**
 * Fetch display names for participant IDs (e.g. for conversation list).
 */
export async function getUsersDisplayNames(uids) {
  if (!uids?.length) return {};
  const out = {};
  await Promise.all(
    uids.map(async (uid) => {
      const snap = await getDoc(doc(db, 'users', uid));
      out[uid] = snap.exists() ? (snap.data().displayName || snap.data().email || 'Angler') : 'Angler';
    })
  );
  return out;
}
